
import { useState, useCallback } from 'react';
import { uploadToCloudinary } from '@/lib/cloudinary';

export interface UploadedImage {
  file: File;
  preview: string;
  url?: string;
  uploading: boolean;
  error?: string;
}

export const useImageUpload = () => {
  const [images, setImages] = useState<UploadedImage[]>([]);
  const [uploading, setUploading] = useState(false);

  const addImages = useCallback((files: File[]) => {
    const newImages = files.map(file => ({
      file,
      preview: URL.createObjectURL(file),
      uploading: false,
    }));
    
    setImages(prev => [...prev, ...newImages]);
  }, []);

  const removeImage = useCallback((index: number) => {
    setImages(prev => {
      const newImages = [...prev];
      URL.revokeObjectURL(newImages[index].preview);
      newImages.splice(index, 1);
      return newImages;
    });
  }, []);

  const uploadImages = useCallback(async (): Promise<string[]> => {
    setUploading(true);
    const uploadPromises = images.map(async (image, index) => {
      if (image.url) return image.url;

      try {
        setImages(prev => {
          const newImages = [...prev];
          newImages[index] = { ...newImages[index], uploading: true };
          return newImages;
        });

        const url = await uploadToCloudinary(image.file);
        // No transformation options for receipts to avoid Cloudinary errors
        
        setImages(prev => {
          const newImages = [...prev];
          newImages[index] = { ...newImages[index], url, uploading: false };
          return newImages;
        });

        return url;
      } catch (error) {
        setImages(prev => {
          const newImages = [...prev];
          newImages[index] = { 
            ...newImages[index], 
            uploading: false, 
            error: 'Upload failed' 
          };
          return newImages;
        });
        throw error;
      }
    });

    try {
      const urls = await Promise.all(uploadPromises);
      setUploading(false);
      return urls;
    } catch (error) {
      setUploading(false);
      throw error;
    }
  }, [images]);

  const reset = useCallback(() => {
    images.forEach(image => URL.revokeObjectURL(image.preview));
    setImages([]);
  }, [images]);

  return {
    images,
    uploading,
    addImages,
    removeImage,
    uploadImages,
    reset,
  };
};
